import { Form, Input, message, Modal, Select } from "antd";
import { useAddDeskMutation, useUpdateDeskMutation } from "features/desksTable/desksAPI";
import {
  useFetchRolesQuery,
  useFetchUserSummariesQuery,
} from "features/userManagementTable/userManagementAPI";

export default function DeskModal({ record, open, onOk, onCancel }) {
  const [form] = Form.useForm();
  const [addDesk] = useAddDeskMutation();
  const [updateDesk] = useUpdateDeskMutation();

  const { data: roles, isLoading: isRolesLoading } = useFetchRolesQuery();
  const { data: users, isLoading: isUsersLoading } = useFetchUserSummariesQuery({
    isActive: true,
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Transform the form data
      const deskAccess = values.deskAccess?.map((item) => JSON.parse(item)) || [];
      const groupIds = [];
      const userIds = [];

      deskAccess.forEach((item) => {
        if (item.type === "role") {
          groupIds.push(item.id);
        } else if (item.type === "user") {
          userIds.push(item.id);
        }
      });

      const apiData = {
        name: values.name,
        notes: values.notes,
        groupIds,
        userIds,
      };

      let response;
      if (record) {
        response = await updateDesk({ id: record.id, ...apiData });
      } else {
        response = await addDesk(apiData);
      }
      if ("data" in response) {
        message.success(`Desk ${record ? "updated" : "created"} successfully`);
        onOk();
      }
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  // Create options with custom value structure
  const deskAccessOptions = [
    {
      label: "Roles",
      options:
        roles?.map((role) => ({
          label: role.name,
          value: JSON.stringify({ type: "role", id: role.id }),
        })) || [],
    },
    {
      label: "Users",
      options:
        users?.map((user) => ({
          label: user.name || user.email || user.id,
          value: JSON.stringify({ type: "user", id: user.id }),
        })) || [],
    },
  ];

  const initialValues = record
    ? {
        name: record.name,
        notes: record.notes,
        deskAccess: [
          ...record.groupIds.map((groupId) => JSON.stringify({ type: "role", id: groupId })),
          ...record.userIds.map((userId) => JSON.stringify({ type: "user", id: userId })),
        ],
      }
    : {};

  return (
    <Modal
      title={record ? "Edit Desk" : "Add Desk"}
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      maskClosable={false}
    >
      <Form form={form} layout="vertical" initialValues={initialValues}>
        <Form.Item
          name="name"
          label="Desk Name"
          rules={[{ required: true, message: "Please enter desk name" }]}
        >
          <Input placeholder="Enter desk name" />
        </Form.Item>

        <Form.Item name="notes" label="Desk Description">
          <Input.TextArea placeholder="Enter desk description" rows={4} />
        </Form.Item>

        <Form.Item
          name="deskAccess"
          label="Desk Access"
          rules={[{ required: true, message: "Please select at least one role or user" }]}
        >
          <Select
            showSearch
            filterOption={(input, option) =>
              (option.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())
            }
            popupMatchSelectWidth={false}
            allowClear
            mode="multiple"
            options={deskAccessOptions}
            placeholder="Select roles or users"
            loading={isRolesLoading || isUsersLoading}
            disabled={isRolesLoading || isUsersLoading}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
